import {create, StateCreator} from 'zustand';
import {
    ActionButtonsModel,
    ActionTypeEnum,
    ItemModel,
    ItemModifierModel, LookupItemModel,
    RequestActionModel,
    ResponseBaseModel,
    RowStateEnum,
    SearchItemApiResponseModel, SearchItemRequestModel, SearchItemsTypeEnum,
    ToastModel,
    ValidationErrorModel
} from "../../models";
import {generateItemStore} from "../../components/items/businessLogic/itemStoresBl";
import React, {Dispatch, SetStateAction} from 'react';
import {getItemDetails} from "../../components/items/businessLogic/itemMiscellaneousBl";
import {deleteItem, getItemByItemInStoreId, searchItems} from "../../serviceBroker/itemApiServiceBroker";
import {getLabelName, isArabicCurrentLanguage} from "../../utils";
//#region interfaces
// Define action button interface
interface ActionButton {
    text: string;
    onClick: () => void | Promise<void>;
}
// Define the store state interface - with both internal _set and public state setters
interface ItemState {
    // State
    loading: boolean;
    formLoading: boolean;
    tableLoading: boolean;
    modalLoading: boolean;
    validationErrors: ValidationErrorModel[];
    item: ItemModel | null;
    toastModel: ToastModel;
    pageName: string;
    showDeleteItemModel: boolean;
    isRefresh: boolean;
    isRefreshItem: boolean;
    isRefreshItemList: boolean;
    isRefreshUnitManager: boolean;
    isRefreshGatherManager: boolean;
    initialValues: ItemModel;
    pageRef: React.RefObject<HTMLDivElement> | null;
    categorySelectBoxMultiselectRef: React.RefObject<any> | undefined;

    // Data states
    categoryList: LookupItemModel[];
    unitList: LookupItemModel[];
    data: SearchItemApiResponseModel[];
    totalRows: number;
    searchItemRequest: SearchItemRequestModel;

    // Tab States
    activeTab: string;
    modifiers: ItemModifierModel[];
    units: any[];
    stores: any[];
    miscellaneous: any[];
    deleteItemActions: ActionButton[];

    // Internal setters (prefixed with underscore)
    _setLoading: (loading: boolean) => void;
    _setFormLoading: (loading: boolean) => void;
    _setTableLoading: (loading: boolean) => void;
    _setModalLoading: (loading: boolean) => void;
    _setValidationErrors: (validationErrors: ValidationErrorModel[]) => void;
    _setItem: (item: ItemModel | null) => void;
    _setToastModel: (toastModel: ToastModel) => void;
    _setPageName: (pageName: string) => void;
    _setShowDeleteItemModel: (showDeleteItemModel: boolean) => void;
    _setIsRefresh: (isRefresh: boolean) => void;
    _setIsRefreshItem: (isRefresh: boolean) => void;
    _setIsRefreshItemList: (isRefresh: boolean) => void;
    _setIsRefreshUnitManager: (isRefresh: boolean) => void;
    _setIsRefreshGatherManager: (isRefresh: boolean) => void;
    _setActiveTab: (activeTab: string) => void;
    _setModifiers: (modifiers: ItemModifierModel[]) => void;
    _setUnits: (units: any[]) => void;
    _setStores: (stores: any[]) => void;
    _setMiscellaneous: (miscellaneous: any[]) => void;
    _setPageRef: (ref: React.RefObject<HTMLDivElement> | null) => void;
    _setDeleteItemActions: (actions: ActionButton[]) => void;
    _setCategoryList: (categories: LookupItemModel[]) => void;
    _setUnitList: (units: LookupItemModel[]) => void;
    _setCategorySelectBoxMultiselectRef: (ref: React.RefObject<any> | undefined) => void;
    _setData: (data: SearchItemApiResponseModel[]) => void;
    _setTotalRows: (total: number) => void;
    _setSearchItemRequest: (request: SearchItemRequestModel) => void;

    // React-compatible setters with dispatch
    setLoading: Dispatch<SetStateAction<boolean>>;
    setFormLoading: Dispatch<SetStateAction<boolean>>;
    setTableLoading: Dispatch<SetStateAction<boolean>>;
    setModalLoading: Dispatch<SetStateAction<boolean>>;
    setValidationErrors: Dispatch<SetStateAction<ValidationErrorModel[]>>;
    setItem: Dispatch<SetStateAction<ItemModel | null>>;
    setToastModel: Dispatch<SetStateAction<ToastModel>>;
    setPageName: Dispatch<SetStateAction<string>>;
    setShowDeleteItemModel: Dispatch<SetStateAction<boolean>>;
    setIsRefresh: Dispatch<SetStateAction<boolean>>;
    setIsRefreshItem: Dispatch<SetStateAction<boolean>>;
    setIsRefreshItemList: Dispatch<SetStateAction<boolean>>;
    setIsRefreshUnitManager: Dispatch<SetStateAction<boolean>>;
    setIsRefreshGatherManager: Dispatch<SetStateAction<boolean>>;
    setActiveTab: Dispatch<SetStateAction<string>>;
    setModifiers: Dispatch<SetStateAction<ItemModifierModel[]>>;
    setUnits: Dispatch<SetStateAction<any[]>>;
    setStores: Dispatch<SetStateAction<any[]>>;
    setMiscellaneous: Dispatch<SetStateAction<any[]>>;
    setPageRef: Dispatch<SetStateAction<React.RefObject<HTMLDivElement> | null>>;
    setDeleteItemActions: Dispatch<SetStateAction<ActionButton[]>>;
    setCategoryList: Dispatch<SetStateAction<LookupItemModel[]>>;
    setUnitList: Dispatch<SetStateAction<LookupItemModel[]>>;
    setCategorySelectBoxMultiselectRef: Dispatch<SetStateAction<React.RefObject<any> | undefined>>;
    setData: Dispatch<SetStateAction<SearchItemApiResponseModel[]>>;
    setTotalRows: Dispatch<SetStateAction<number>>;
    setSearchItemRequest: Dispatch<SetStateAction<SearchItemRequestModel>>;

    // Action handlers
    handleAction: (request: RequestActionModel) => Promise<void>;
    getItemById: (id: number) => ItemModel | null;
    getItemByStoreId: (itemInStoreId: number) => Promise<ResponseBaseModel<ItemModel>>;
    getItemDetails: (itemInStoreId: number) => Promise<any>;
    saveItem: (item: ItemModel) => void;
    updateItem: (id: number, updates: Partial<ItemModel>) => void;
    deleteItem: (id: number) => boolean;
    resetItem: () => void;
    scrollToTop: (ref: React.RefObject<HTMLDivElement> | null) => void;
    reValidateLocalStorageCachedData: (lookupType: any) => Promise<void>;
    // Additional methods for table management
    getItems: () => Promise<void>;
    // Additional methods for item actions
    deleteItemAction: (id: number) => ActionButtonsModel[];
}
//#endregion
//#region variables
// Initial model values
const initialItemValues: Readonly<ItemModel> = {
    Code: "",
    Name_En: "",
    Name: "",
    Notes: "",
    Cat_ID: 0,
    StagnantPeriod: 0,
    UserID: 0,
    TaxValue: null,
    OrderLimit: 0,
    OpenPrice: false,
    IsActive: true,
    DisplayIndex: 0,
    TobaccoTax: false,
    TobaccoValue: 0,
    UseExpiryDate: false,
    ExpireDay: 0,
    ShowInPOS: true,
    GatherItem: false,
    OpenItem: false,
    WithoutBalance: false,
    HasModifiers: false,
    CanAddAddationalCost: false,
    HasImage: false,
    IsProduction: false,
    HasSerial: false,
    Status: true,
    IsIncludeTobaccoTax: false,
    TobaccoTaxPercentage: 0,
    IsUnisSeparateInBalance: false,
    IsItemVanishConsign: false,
    ItemsInstores: generateItemStore(),
    DeleteOldSaveItemUnitGather: false,
    IsUpdateItemPriceInOtherStores: false,
    UpdateGatherItemCost: false,
    IsDifferentFactor: false,
    Item_Supplier_ID: 0,
    Is_Filter_Service: false,
    Is_Oil_Service: false,
    DisplySequence: 0,
    ID: 0,
    CreatedBy: 0,
    ModifiedBy: 0,
    Item_Modifiers: [],
    CreationDate: "",
    ModificationDate: "",
    VerifyOnUpdate: false,
    rowState: Number(RowStateEnum.Add),
    ItemImages: null,
    isNewItem: true
};
//#endregion
//#region functions
function createReactSetter<T>(setter: (value: T) => void, getState: () => T): Dispatch<SetStateAction<T>> {
    return (value) => {
        if (typeof value === 'function') {
            const updaterFn = value as (prevState: T) => T;
            const currentState = getState();
            setter(updaterFn(currentState));
        } else {
            setter(value);
        }
    };
}
//#endregion

const createItemStore: StateCreator<ItemState> = (set, get) => {
    return {
        // State
        loading: false,
        formLoading: false,
        tableLoading: false,
        modalLoading: false,
        validationErrors: [],
        item: { ...initialItemValues } as ItemModel,
        toastModel: {
            show: false,
            body: "Process completed successfully",
            variant: "success",
        },
        pageName: '',
        showDeleteItemModel: false,
        isRefresh: false,
        isRefreshItem: false,
        isRefreshItemList: false,
        isRefreshUnitManager: false,
        isRefreshGatherManager: false,
        initialValues: initialItemValues,
        pageRef: null,
        categorySelectBoxMultiselectRef: undefined,

        // Data states
        categoryList: [],
        unitList: [],
        data: [],
        totalRows: 0,
        searchItemRequest: {
            pageNumber: 1,
            pageSize: 10,
            searchType: SearchItemsTypeEnum.ItemName,
            smallestUnit: true,
            searchValue: "",
            code: "",
            name: "",
        },


        // Tab States
        activeTab: "general",
        modifiers: [],
        units: [],
        stores: [],
        miscellaneous: [],
        deleteItemActions: [],

        // Internal setters (prefixed with underscore)
        _setLoading: (loading) => set({ loading }),
        _setFormLoading: (loading) => set({ formLoading: loading }),
        _setTableLoading: (loading) => set({ tableLoading: loading }),
        _setModalLoading: (loading) => set({ modalLoading: loading }),
        _setValidationErrors: (validationErrors) => set({ validationErrors }),
        _setItem: (item) => set({ item }),
        _setToastModel: (toastModel) => set({ toastModel }),
        _setPageName: (pageName) => set({ pageName }),
        _setShowDeleteItemModel: (showDeleteItemModel) => set({ showDeleteItemModel }),
        _setIsRefresh: (isRefresh) => set({ isRefresh }),
        _setIsRefreshItem: (isRefresh) => set({ isRefreshItem: isRefresh }),
        _setIsRefreshItemList: (isRefresh) => set({ isRefreshItemList: isRefresh }),
        _setIsRefreshUnitManager: (isRefresh) => set({ isRefreshUnitManager: isRefresh }),
        _setIsRefreshGatherManager: (isRefresh) => set({ isRefreshGatherManager: isRefresh }),
        _setActiveTab: (activeTab) => set({ activeTab }),
        _setModifiers: (modifiers) => set({ modifiers }),
        _setUnits: (units) => set({ units }),
        _setStores: (stores) => set({ stores }),
        _setMiscellaneous: (miscellaneous) => set({ miscellaneous }),
        _setPageRef: (ref) => set({ pageRef: ref }),
        _setDeleteItemActions: (actions) => set({ deleteItemActions: actions }),
        _setCategoryList: (categories) => set({ categoryList: categories }),
        _setUnitList: (units) => set({ unitList: units }),
        _setCategorySelectBoxMultiselectRef: (ref) => set({ categorySelectBoxMultiselectRef: ref }),
        _setData: (data) => set({ data }),
        _setTotalRows: (total) => set({ totalRows: total }),
        _setSearchItemRequest: (request) => set({ searchItemRequest: request }),

        // React-compatible setters with dispatch
        setLoading: createReactSetter((value) => set({ loading: value }), () => get().loading),
        setFormLoading: createReactSetter((value) => set({ formLoading: value }), () => get().formLoading),
        setTableLoading: createReactSetter((value) => set({ tableLoading: value }), () => get().tableLoading),
        setModalLoading: createReactSetter((value) => set({ modalLoading: value }), () => get().modalLoading),
        setValidationErrors: createReactSetter((value) => set({ validationErrors: value }), () => get().validationErrors),
        setItem: createReactSetter((value) => set({ item: value }), () => get().item),
        setToastModel: createReactSetter((value) => set({ toastModel: value }), () => get().toastModel),
        setPageName: createReactSetter((value) => set({ pageName: value }), () => get().pageName),
        setShowDeleteItemModel: createReactSetter((value) => set({ showDeleteItemModel: value }), () => get().showDeleteItemModel),
        setIsRefresh: createReactSetter((value) => set({ isRefresh: value }), () => get().isRefresh),
        setIsRefreshItem: createReactSetter((value) => set({ isRefreshItem: value }), () => get().isRefreshItem),
        setIsRefreshItemList: createReactSetter((value) => set({ isRefreshItemList: value }), () => get().isRefreshItemList),
        setIsRefreshUnitManager: createReactSetter((value) => set({ isRefreshUnitManager: value }), () => get().isRefreshUnitManager),
        setIsRefreshGatherManager: createReactSetter((value) => set({ isRefreshGatherManager: value }), () => get().isRefreshGatherManager),
        setActiveTab: createReactSetter((value) => set({ activeTab: value }), () => get().activeTab),
        setModifiers: createReactSetter((value) => set({ modifiers: value }), () => get().modifiers),
        setUnits: createReactSetter((value) => set({ units: value }), () => get().units),
        setStores: createReactSetter((value) => set({ stores: value }), () => get().stores),
        setMiscellaneous: createReactSetter((value) => set({ miscellaneous: value }), () => get().miscellaneous),
        setPageRef: createReactSetter((value) => set({ pageRef: value }), () => get().pageRef),
        setDeleteItemActions: createReactSetter((value) => set({ deleteItemActions: value }), () => get().deleteItemActions),
        setCategoryList: createReactSetter((value) => set({ categoryList: value }), () => get().categoryList),
        setUnitList: createReactSetter((value) => set({ unitList: value }), () => get().unitList),
        setCategorySelectBoxMultiselectRef: createReactSetter((value) => set({ categorySelectBoxMultiselectRef: value }), () => get().categorySelectBoxMultiselectRef),
        setData: createReactSetter((value) => set({ data: value }), () => get().data),
        setTotalRows: createReactSetter((value) => set({ totalRows: value }), () => get().totalRows),
        setSearchItemRequest: createReactSetter((value) => set({ searchItemRequest: value }), () => get().searchItemRequest),

        // Action handlers
        handleAction: async (request) => {
            const state = get();
            //state.setValidationErrors([]);
            //state.setTableLoading(true)
            set({ validationErrors: [] });
            const isArabic = isArabicCurrentLanguage();
            switch (request.action) {
                case ActionTypeEnum.Add:
                    set({ item: null });
                    break;
                case ActionTypeEnum.Update:
                    set({ formLoading: true, tableLoading: true });
                    const item = await getItemDetails(request.request.ItemInstore_ID!);
                    if (item.Errors?.length !== 0) {
                        set({ validationErrors: item.Errors ?? [] });
                        return;
                    }
                    set({
                        item: item.Result,
                        isRefreshItem: true,
                        tableLoading: false,
                        formLoading: false
                    });
                    state.scrollToTop(state.pageRef);
                    break;
                case ActionTypeEnum.Delete:
                    set({ item: null });
                    const deleteObject = (
                        await getItemByItemInStoreId(request.request.ItemInstore_ID)
                    ).Result;
                    set({
                        item: deleteObject ?? null,
                        showDeleteItemModel: true
                    });
                    break;
                case ActionTypeEnum.AddSuccess:
                case ActionTypeEnum.DeleteSuccess:
                case ActionTypeEnum.Success:
                    set({
                        toastModel: { ...state.toastModel, show: true, variant: "success" },
                        isRefreshItemList: true,
                        isRefreshItem: true,
                        item: null,
                        showDeleteItemModel: false
                    });
                    await state.reValidateLocalStorageCachedData("Items");
                    state.scrollToTop(state.pageRef);
                    break;
                case ActionTypeEnum.RaiseError:
                    set({ validationErrors: request.request });
                    // setToastModel((prev) => ({
                    //     ...prev,
                    //     show: true,
                    //     variant: "danger",
                    //
                    //     body:
                    //         request?.request?.at(0)?.[isArabic ? "MessageAr" : "MessageEn"] ??
                    //         "Some Thing Went Wrong",
                    // }));
                    state.scrollToTop(state.pageRef);
                    break;
                case ActionTypeEnum.DeleteFailed:
                    set({
                        showDeleteItemModel: false,
                        validationErrors: request.request
                    });
                    state.scrollToTop(state.pageRef);
                    break;
                case ActionTypeEnum.Clear:
                    set({ item: null });
                    state.scrollToTop(state.pageRef);
                    break;
                case ActionTypeEnum.ScrollToTop:
                    state.scrollToTop(state.pageRef);
                    break;
                default:
                    console.warn(`Unknown action: ${request.action}`);
            }
        },
        getItems: async () => {
            const state = get();
            state.setTableLoading(true);

            try {
                const result = await searchItems(state.searchItemRequest);
                set({
                    data: result?.Result || [],
                    totalRows: result?.TotalRecoredCount || 0,
                    validationErrors: result?.Errors || [],
                    isRefreshItemList: false
                });
            } catch (error) {
                console.error("Error fetching items:", error);
                set({
                    data: [],
                    validationErrors:[{MessageAr:`Failed to load lookup data: ${error}`,MessageEn:`Failed to load lookup data: ${error}`}],
                    totalRows: 0
                });
            } finally {
                state.setTableLoading(false);
            }
        },
        getItemById: (id) => {
            const state = get();
            return state.item?.ID === id ? state.item : null;
        },
        getItemByStoreId: async (itemInStoreId) => {
            // Call the actual API function
            return await getItemByItemInStoreId(itemInStoreId);
        },
        getItemDetails: async (itemInStoreId) => {
            // Call the actual API function
            return await getItemDetails(itemInStoreId);
        },
        saveItem: (item) => {
            set({ item });
        },
        updateItem: (id, updates) => {
            const state = get();
            if (state.item?.ID === id) {
                set({ item: { ...state.item, ...updates } });
            }
        },
        deleteItem: (id) => {
            set({ showDeleteItemModel: false });
            return true; // Indicate successful deletion
        },
        resetItem: async () => {
            const state = get();
             await  state.handleAction({
                action: ActionTypeEnum.Clear,
                request: null
            });
        },
        // Delete item action handler
        deleteItemAction: (id: number) => {
            const state = get();

            return [
                {
                    text: getLabelName("yes"),
                    onClick: async () => {
                        state.setModalLoading(true);
                        const result = await deleteItem(id);
                        state.setModalLoading(false);
                        const isSuccess = result.Result == true;
                        await state.handleAction({
                            request:isSuccess ? null : result?.Errors,
                            action: isSuccess
                                ? ActionTypeEnum.Success
                                : ActionTypeEnum.DeleteFailed,
                        });
                    },
                },
                {
                    text: getLabelName("no"),
                    onClick: () => {
                        state.setItem(null);
                        state.setShowDeleteItemModel(false);
                    },
                },
            ];
        },
        scrollToTop: (ref) => {
            if (ref && ref.current) {
                ref.current.scrollIntoView({ behavior: 'smooth' });
            }
        },
        reValidateLocalStorageCachedData: async (lookupType) => {
            // Mock implementation for cache revalidation
            console.log(`Revalidating cache for lookup type: ${lookupType}`);
        }
    };
};

// Create and export the store
export const useItemManager = create<ItemState>(createItemStore);

// Export as default
//export default useItemManager;